/**
 * frontend/src/pages/Contact.js
 * 
 * Public contact page for ChangeFox.
 * Provides multiple ways for users and prospects to get in touch with the team,
 * including email and support channels.
 */
import React from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { FiMail, FiMessageSquare, FiClock } from 'react-icons/fi';
import BackgroundShapes from '../components/common/BackgroundShapes';
import Footer from '../components/common/Footer';
import SEO from '../components/common/SEO';
import logo from '../assets/logo.png';
import '../styles/landing.css';

const Contact = () => {
  const contactMethods = [
    {
      icon: <FiMail />,
      title: 'Email Us',
      description: 'Send us an email and we\'ll respond within 24 hours.',
      detail: 'contact@changefox.ai',
      link: 'mailto:contact@changefox.ai',
      color: '#6366f1',
    },
    {
      icon: <FiMessageSquare />,
      title: 'Support',
      description: 'Get help with technical issues or questions about your account.',
      detail: 'contact@changefox.ai',
      link: 'mailto:contact@changefox.ai',
      color: '#8b5cf6',
    },
    {
      icon: <FiClock />,
      title: 'Business Hours',
      description: 'Our team is available Monday through Friday.',
      detail: '9:00 AM - 6:00 PM EST',
      color: '#06b6d4',
    },
  ];

  return (
    <div className="landing-page">
      <SEO
        title="Contact Us - ChangeFox"
        description="Get in touch with the ChangeFox team. We're here to help with any questions about our compliance management platform."
        keywords="contact, support, help, changefox, customer service"
        path="/contact"
      />
      <BackgroundShapes />

      {/* Navigation */}
      <motion.nav
        className="landing-nav"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Container>
          <div className="nav-content">
            <Link to="/" className="nav-logo">
              <img src={logo} alt="ChangeFox" className="nav-logo-img" />
            </Link>
            <div className="nav-links">
              <Link to="/" className="nav-link">Home</Link>
              <Link to="/login" className="nav-link">Sign In</Link>
            </div>
          </div>
        </Container>
      </motion.nav>

      {/* Content */}
      <section className="legal-section">
        <Container>
          <motion.div
            className="section-header"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="section-badge light">Contact</div>
            <h1 className="section-title light">Get in Touch</h1>
            <p className="section-subtitle light">
              Have questions about ChangeFox? We're here to help. Reach out to us through any of the channels below.
            </p>
          </motion.div>

          <Row className="g-4 mt-4">
            {contactMethods.map((method, index) => (
              <Col key={index} md={4}>
                <motion.div
                  className="contact-card"
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  whileHover={{ y: -8, transition: { duration: 0.2 } }}
                >
                  <div
                    className="contact-icon"
                    style={{ background: `${method.color}15`, color: method.color }}
                  >
                    {method.icon}
                  </div>
                  <h3 className="contact-title">{method.title}</h3>
                  <p className="contact-description">{method.description}</p>
                  {method.link ? (
                    <a href={method.link} className="contact-detail-link">
                      {method.detail}
                    </a>
                  ) : (
                    <p className="contact-detail">{method.detail}</p>
                  )}
                </motion.div>
              </Col>
            ))}
          </Row>

          <motion.div
            className="contact-cta"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
          >
            <h2>Ready to get started?</h2>
            <p>Sign up for a free trial and experience compliance management made simple.</p>
            <Link to="/register" className="btn-hero-primary">
              Start Free Trial
            </Link>
          </motion.div>
        </Container>
      </section>

      <Footer />
    </div>
  );
};

export default Contact;
